/**
 * What the green room is looking at, and what it says about it (web-ui ticket
 * 05).
 *
 * The decisions, with no DOM and no contract in them — `control-bar.ts` is the
 * precedent and the reason is the same one. The room's camera stage draws one of
 * five things and four of them are prose: a screen whose only visible difference
 * between "you have never been asked" and "you said no" is a sentence is a
 * screen where being wrong looks like being right, and the person it is wrong
 * for is a first-time visitor being sent to un-block something they never
 * blocked.
 *
 * The classification deliberately does **not** read the error message.
 * `captureFailureMessage` is shared with the desktop, merges `NotAllowedError`
 * with `SecurityError`, and phrases the refusal as "Windows is blocking access
 * to the camera or microphone. Allow desktop apps to use them in Settings →
 * Privacy & security" — advice a browser tab cannot act on and a classifier
 * nothing should be built on. What the browser will answer directly is used
 * instead: the permission, the cameras it admits exist, and whether a picture
 * turned up.
 */

/**
 * The five the brief draws, and the two web-ui ticket 06 adds.
 *
 * `camera-off` and `opening` **draw the same thing** — the stage fills and a
 * circle stands where the picture goes — and they are still two states, because
 * everything else on the screen has to tell them apart. One is a choice somebody
 * made and the other is a camera waking up: only the first gets the line under
 * the stage about audio-only takes, and only the first is a camera the switch
 * over the preview should be showing as off.
 *
 * The temptation is to fold them into one name, since the pixels are identical.
 * That was tried and it produced exactly the disagreement this screen is supposed
 * not to have: a bar drawing the camera as *on* beside a stage drawing it as
 * deliberately *off*, for the second it takes a granted camera to open.
 */
export type CameraStage =
  | "first-visit"
  | "requesting"
  | "live"
  | "blocked"
  | "no-hardware"
  | "camera-off"
  | "opening";

/**
 * What `navigator.permissions` says, plus the answer for a browser that will not
 * say at all.
 *
 * `unknown` is not a fourth state on screen — it behaves as `prompt` — but it
 * has to exist here, because the query is absent in some engines and its
 * descriptor is not in the DOM type union. Collapsing it into `prompt` at the
 * call site would have hidden the fact that a whole input can be missing.
 */
export type CameraPermission = "granted" | "prompt" | "denied" | "unknown";

export interface CameraInputs {
  permission: CameraPermission;
  /**
   * Whether a preview is being asked for right now — the visitor pressed the
   * button, or this is a return visit and the room asked silently on arrival.
   *
   * Distinct from `permission`, because the engine drops the standing request
   * when a device refuses it (`#previewRefused`), so a refusal is exactly the
   * state where permission has moved and this has gone back to false.
   */
  asked: boolean;
  /**
   * Whether there is a **picture** — a live video track, not merely a stream.
   *
   * The distinction is what "No camera found" needs to survive. A machine with a
   * microphone and no camera gets an audio-only preview, on purpose: asking for a
   * camera that is not there fails the whole open and takes the microphone with
   * it, and "you can still record audio" would be a sentence under a dead
   * microphone. So there is a stream, and there is still nothing to look at.
   */
  streaming: boolean;
  /** `CapturePreview.error` — the engine's sentence, used as detail and never
   * as a classifier. */
  error: string | null;
  /**
   * How many cameras the browser admits exist, or null before the first
   * enumeration — `DeviceState.cameras`, which is trustworthy with or without
   * permission because it counts entries rather than identities.
   */
  cameras: number | null;
  /**
   * Whether the camera has been switched off (web-ui ticket 06) —
   * `DeviceState.off`, which is the worker's answer and not a second copy of it
   * held in the page. On the switch over the preview since flow ticket 04, and on
   * the prompter's control bar; both write the same field.
   *
   * A press has to be felt before any of the readings below can have moved, so
   * this comes first in {@link cameraStage} and beats even a picture: the video
   * track goes in the same turn, but "the room does what I pressed" cannot wait
   * on a device.
   */
  cameraOff: boolean;
}

/**
 * Which of the six, in strict precedence — the order is the whole rule.
 *
 * Four of the steps carry the weight:
 *
 * - **A press beats every reading**, including the picture that is still on
 *   screen for the turn it takes to drop the track. Everything else here is the
 *   browser answering a question; only this is something the user just did.
 * - **A picture beats everything else**, including an error left behind by a
 *   refusal that was afterwards recovered from. A room that read the error first
 *   would cover a working camera with an apology for it.
 * - **No camera beats every permission question**, because there is nothing to
 *   grant. It has to come first or a machine with no camera can never say so: the
 *   camera permission is never asked for, so it never leaves `prompt`, and a rule
 *   that waited for `granted` would sit on "Waiting for the browser…" forever.
 * - **A denied permission beats an outstanding request**, because permission is
 *   the fact that survives a reload and a request is not.
 *
 * And what is left at the bottom is the difference between a browser that has not
 * answered and one that answered yes a visit ago: only the first is a prompt
 * worth waiting on, and the second is a camera opening.
 */
export function cameraStage(inputs: CameraInputs): CameraStage {
  const { permission, asked, streaming, error, cameras, cameraOff } = inputs;
  // A press beats every reading, including the picture that is still there for
  // the turn it takes to drop the track. Nothing else on this screen is a thing
  // the user just did.
  if (cameraOff) return "camera-off";
  if (streaming) return "live";
  if (cameras === 0) return "no-hardware";
  if (permission === "denied") return "blocked";
  if (error !== null) return "blocked";

  // Granted and no picture yet: the camera is *opening*, on arrival or on its way
  // back from being switched off, and there is no browser being waited on — the
  // browser has already said yes. So the slot stands where the picture goes, the
  // way it does in every video call, rather than a prompt-shaped message about a
  // prompt nobody is looking at. It is also what makes turning the camera back on
  // seamless: the same thing is drawn on both sides of the gap.
  if (permission === "granted") return "opening";
  if (asked) return "requesting";
  return "first-visit";
}

/**
 * What the page should be holding open, given all of that.
 *
 * Pure and separate from the component because each of these has a silent failure
 * behind it, and the one that matters most cannot be reached from a browser test
 * at all.
 *
 * - **`retrying` wins over everything.** A retry only works by asking for
 *   *nothing* first — `CaptureEngine` remembers what the hardware turned down and
 *   ignores a repeat of it, which is what stops a refused camera becoming an open
 *   per frame. Deriving the pause from "the user has stopped asking" is the trap:
 *   on a *granted* permission the room asks anyway, so the pause never happens and
 *   Try again does nothing at all. That is the state a camera held by another
 *   application is in, and it is reachable only by having a second application
 *   open the camera.
 * - **Nothing at all until the devices have been enumerated**, because the
 *   constraints a take will use come from the same enumeration: opening before it
 *   lands means opening on the system default and then reopening on the named
 *   device, which is FR-31's invariant broken and a camera light blinking on
 *   arrival.
 */
export function previewRequest<T>(input: {
  /** The constraints a take would use, or null before the first enumeration.
   * A camera known not to be there is already absent from them — that rule lives
   * in `captureConstraints`, once, so the preview and the take cannot disagree
   * about whether this machine has one. */
  wanted: T | null;
  /** Whether the room wants a camera at all — pressed, or granted on an earlier
   * visit. */
  wantsPreview: boolean;
  /** The half-turn of a retry, in which the answer is always nothing. */
  retrying: boolean;
}): T | null {
  const { wanted, wantsPreview, retrying } = input;
  if (retrying || !wantsPreview || wanted === null) return null;
  return wanted;
}

/** One control in the camera well, where the state has something to press. */
export interface WellAction {
  label: string;
  /** `grant` asks for the first time; `retry` re-asks after a refusal, which the
   * engine only honours if the standing want is cleared first. */
  kind: "grant" | "retry";
}

/** What fills the camera stage when there is no picture in it. */
export interface StageWell {
  glyph: "camera" | "camera-off" | "dots" | "avatar";
  /**
   * The heading, where there is one to say.
   *
   * Absent for the avatar slot alone, and that is the point of it: an empty black
   * rectangle looks like a fault and a deliberate placeholder looks like a choice,
   * and a placeholder with a sentence under it explaining itself has gone back to
   * looking like a fault. The camera being off is said *under* the stage instead,
   * where the consequence is — see {@link CAMERA_OFF_NOTE}.
   */
  says?: string;
  detail?: string;
  action?: WellAction;
}

const NOTHING_LEAVES =
  "Handled entirely in this browser. Nothing is uploaded.";

const UNBLOCK =
  "Click the camera icon in your address bar and allow access, then reload.";

/**
 * The well, or null once the video is up.
 *
 * `error` is taken as *detail* rather than as a state. A camera held by another
 * application is blocked in the same sense as one that was refused and for a
 * completely different reason, and telling that person to allow access in the
 * address bar sends them to a setting that is already correct — so where the
 * engine has a sentence of its own it wins over the generic one.
 */
export function stageWell(
  stage: CameraStage,
  error: string | null,
): StageWell | null {
  switch (stage) {
    case "live":
      return null;
    case "camera-off":
    case "opening":
      // Meet's avatar slot, and for Meet's reason. The one place in this file
      // where the two states are the same answer — which is the point of them
      // being two states everywhere else.
      return { glyph: "avatar" };
    case "first-visit":
      return {
        glyph: "camera",
        says: "See yourself before you record",
        detail: NOTHING_LEAVES,
        action: { label: "Turn on camera and mic", kind: "grant" },
      };
    case "requesting":
      // Nothing to press: the browser's own prompt is the control, and a second
      // button beside it would be one that cancels nothing.
      return { glyph: "dots", says: "Waiting for the browser…" };
    case "blocked":
      return {
        glyph: "camera-off",
        says: "Camera and microphone are blocked",
        detail: error ?? UNBLOCK,
        action: { label: "Try again", kind: "retry" },
      };
    case "no-hardware":
      // No retry. Re-asking for hardware that is not plugged in is a button that
      // always fails, and the sentence is about what still works instead.
      return {
        glyph: "camera-off",
        says: "No camera found",
        detail:
          "You can still record audio, or use the prompter without recording.",
      };
  }
}

/* ---- The two devices you can switch off (web-ui ticket 06) ---- */

/**
 * Under the stage, when the camera is off.
 *
 * Under it rather than in it, because it is not about the hole where the picture
 * was — it is about the take you are still about to record, and it has to be
 * readable at the moment you press record rather than only while you are looking
 * at the empty stage.
 */
export const CAMERA_OFF_NOTE = "Camera is off — takes will record audio only.";

/**
 * Under the preview when the microphone is off, and above the prompter's control
 * bar (flow ticket 04 — it rode that bar's dock on both screens until the room
 * lost its bar; one constant, two renderings).
 *
 * **Two consequences in one sentence, and the order is deliberate.** The second
 * is obvious — a muted microphone records no sound — and the first is the one
 * that will actually surprise somebody: the prompter follows your voice, so a
 * microphone switched off is a prompter that has stopped moving, three minutes
 * into a read, for a reason nothing else on the screen explains.
 */
export const MIC_OFF_NOTE =
  "Microphone is off — the prompter can't follow your voice, and the take has no sound.";

/** Why the two circles are dead on a screen that has been refused the hardware —
 * the same fix the stage beside them is offering, said in a tooltip. */
export const BLOCKED_TOGGLE_REASON = "Camera and microphone are blocked";

/* ---- Filling the box from a Google Doc (flow ticket 03) ----
 *
 * The button's label, the sharing hint and the overwrite question live in
 * `@cuekards/core/script-copy` since mobile-app ticket 14, which gave the phone's
 * green room the same dialog. Nothing about the words changed; what changed is
 * that two screens say them. */

/* ---- The room's one action (flow ticket 05) ---- */

/**
 * The one thing this screen does.
 *
 * It hands whatever is in the box to `loadScript` and opens the prompter, and it
 * **does not start the take** — the record button on the prompter's control bar
 * does that, when the reader is ready. There is a gap between having a script and
 * being ready to read it to camera — getting into frame, checking the shot,
 * finding the top of the page — and the room is the wrong side of the door to
 * spend it on.
 *
 * It replaces two controls with one: the `Load script` button flow ticket 03 took
 * away, and the record button that went with the control bar in ticket 04. "Load"
 * was never a thing anybody wanted to do; it was a thing the product needed doing
 * before it would let you record.
 *
 * Stored **as written** rather than shouted by `text-transform`. The tests in this
 * repository assert on constants, and a constant saying one thing while the screen
 * says another is a label nothing can check.
 */
export const START_RECORDING = "START RECORDING";

/**
 * Why it is dead over an empty box.
 *
 * **Deliberately not `NO_SCRIPT_REASON`, and the difference is which fact each of
 * them is about.** That one is the control bar's, and the bar is the prompter's
 * alone since flow ticket 04 — a screen with no box, where "Load or paste a script
 * first" means a script that was never loaded and names a thing to go and do. Here
 * the box *is* the script and it is two inches away, so the sentence names the two
 * ways of filling it rather than a `Load script` step that no longer exists. One
 * sentence covering both would have to be vague enough to be true on a screen with
 * no box, which is how a refusal stops being actionable.
 *
 * The rule it explains is `trim()`-empty rather than `""`-empty, and that is
 * `PastedScriptSource`'s own refusal rather than a second one: a box holding
 * nothing but spaces is refused there as `empty`, so a button live over it would
 * be a press whose only possible answer is an error.
 */
export const EMPTY_BOX_REASON = "Paste a script, or load one from a Google Doc";

/** One of the two device circles, or null where there is no device for it to be
 * about. A host adds the press; what is decided here is whether there is anything
 * to press and what it would be saying. */
export interface ToggleState {
  on: boolean;
  /**
   * Why this circle cannot be pressed, where it cannot — and it is still *drawn*,
   * which is the whole difference between this and handing over no circle at all.
   *
   * The brief's state table asks for it in one place: a blocked camera shows "the
   * blocked state (red slash)" rather than a plain grey glyph, because a browser
   * that refused the hardware is not the same thing as a machine that has none,
   * and the two would otherwise be one drawing.
   */
  refused?: string;
}

/** What the two device circles are, on a room in a given state. */
export interface RoomToggles {
  camera: ToggleState | null;
  microphone: ToggleState | null;
}

/**
 * Which of the two devices can be switched, and which way they are set (web-ui
 * ticket 06).
 *
 * One answer for both surfaces since flow ticket 04: the room draws these over
 * its preview and the prompter draws them on its control bar, out of the same
 * call. `stageControls` below reads the same answer to decide whether the room's
 * overlay is drawn at all.
 *
 * An earlier draft of ticket 05 left a `deviceToggles(stage)` behind for this
 * ticket and it was deleted rather than shipped unused, because its question was
 * "which devices are open" and this one's is "what does the bar do about a device
 * the user has switched off". They differ exactly where it matters: a camera that
 * is off is not open and still has a live button, because pressing it is how you
 * get it back.
 *
 * The rule is one sentence — **a device you have been granted has a switch, and a
 * device you have not does not** — with two states that are neither. `blocked`
 * has no switch and still has something to say, so it draws one and refuses it;
 * `no-hardware` has a microphone and no camera, so it is the only state where the
 * two circles disagree with each other. Both of those are why `stageControls` is
 * asked of this answer rather than of the stage: neither is guessable from the
 * name.
 */
export function roomToggles(input: {
  stage: CameraStage;
  cameraOn: boolean;
  micOn: boolean;
}): RoomToggles {
  const { stage, cameraOn, micOn } = input;
  switch (stage) {
    case "first-visit":
    case "requesting":
      // Nothing has been handed over yet, so nothing here pretends there is
      // anything to hand back. The stage beside it is already offering the only
      // press that would help.
      return { camera: null, microphone: null };
    case "blocked": {
      // Dead, but *drawn* as blocked rather than as absent — the brief's state
      // table asks for the red slash here. A browser that refused the hardware is
      // not the same thing as a machine that has none, and a plain grey glyph
      // would be the only drawing either of them got.
      const refused = { on: false, refused: BLOCKED_TOGGLE_REASON };
      return { camera: refused, microphone: refused };
    }
    case "no-hardware":
      // The one state where the two disagree, and the reason they are decided
      // together rather than one rule each.
      return { camera: null, microphone: { on: micOn } };
    case "live":
    case "camera-off":
    case "opening":
      return { camera: { on: cameraOn }, microphone: { on: micOn } };
  }
}

/**
 * Whether the preview has anything pressable to draw over it (flow ticket 04).
 *
 * **Derived from {@link roomToggles} rather than from a list of stage names**, and
 * that is the whole of why it exists. Ticket 04 asked for the overlay on `live` and
 * `camera-off`, "which is where `roomToggles` hands over anything pressable" — and
 * that premise is wrong in two places, so following the list would have shipped two
 * bugs:
 *
 * - **`no-hardware` hands over a live microphone.** A machine with no camera is the
 *   one state where the two circles disagree, and the microphone is still yours to
 *   switch. Drawing on the two named stages would have taken that switch off the
 *   screen altogether — it was on the control bar until this ticket, so it is a
 *   control the room *lost* rather than one it never had.
 * - **`opening` hands over both, and draws the same slot `camera-off` draws.** That
 *   pairing is deliberate (web-ui ticket 06) and is what makes turning the camera
 *   back on seamless; a rule naming one of the two would blink the switches out for
 *   the length of an open, which on a real camera is hundreds of milliseconds.
 *
 * What the ticket's *reason* asks for is the thing this answers: the overlay must
 * not appear over a well that is offering a press of its own — "a grant button and
 * two dead circles in the same box is two answers to one question". Those are the
 * states where nothing is pressable, so the reason and the rule are the same
 * sentence once it is asked of the toggles instead of the stage.
 *
 * A *refused* circle counts as nothing pressable. `blocked` draws both as a red
 * slash on the control bar, where the bar is all there is; here the well behind
 * them already says "Camera and microphone are blocked" and offers Try again, and
 * two dead circles on top of that is the second answer the ticket forbids.
 */
export function stageControls(toggles: RoomToggles): boolean {
  return [toggles.camera, toggles.microphone].some(
    (toggle) => toggle !== null && toggle.refused === undefined,
  );
}

/* ---- The room's one action, as a decision (flow ticket 05) ---- */

/** What the room's primary button says and whether it can be pressed. */
export interface StartButton {
  label: string;
  disabled: boolean;
  /** Why it is dead, where that is a thing a user can act on — which is the empty
   * box and not a load already in flight. */
  reason?: string;
}

/**
 * The room's one action, for the state the box is in.
 *
 * Here rather than in the component for the reason `recordButton` is in
 * `control-bar.ts`: it is a decision with two refusals and a label, and the same
 * answer is needed twice — the button draws it, and {@link StartButton.disabled}
 * is also what the host's own handler guards on, so a press that arrived through
 * the `disabled` attribute cannot reach a refusal the screen never showed.
 *
 * `trim()` rather than `""`, and that is `PastedScriptSource`'s own `empty`
 * refusal rather than a second copy of it: a button live over a box of spaces is a
 * press whose only possible answer is an error.
 *
 * The in-flight case is dead but says nothing. A load takes a moment and a second
 * press would start a second one; there is nothing for the reader to *do* about
 * that, and a tooltip explaining it would be a sentence in the way. It keeps the
 * button's shape rather than removing it, so the column does not move under a hand.
 */
export function startButton(input: {
  text: string;
  loading: boolean;
}): StartButton {
  const { text, loading } = input;
  if (loading) return { label: "Loading…", disabled: true };
  if (text.trim() === "")
    return { label: START_RECORDING, disabled: true, reason: EMPTY_BOX_REASON };
  return { label: START_RECORDING, disabled: false };
}

/* ---- Follow my voice, as a decision (flow ticket 06) ---- */

/**
 * The name the feature has never had.
 *
 * Speech-following is the only reason to use cuekards over any other
 * teleprompter, and until this ticket it was a panel offering a download and an
 * unpersisted enum behind a `⋯` row called "Cycle mode". Named for what it does
 * for the reader, not for the thing that makes it work: nobody arrives wanting a
 * speech model, and "Follow my voice" is the sentence somebody would use to
 * describe why they picked this over the free one.
 */
export const FOLLOW_MY_VOICE = "Follow my voice";

/** The dialog's heading. The same words as the box, because it is the box's
 * question being asked in full rather than a second subject. */
export const FOLLOW_MODAL_TITLE = FOLLOW_MY_VOICE;

/**
 * What following actually is, said once before the size is quoted.
 *
 * Deliberately not about the model. What is being agreed to is a download, and
 * what it buys is the product's whole reason for existing plus every later visit
 * for nothing — so this says what it does and where the audio goes, and
 * {@link followModal} puts the panel's own sentence about the cost underneath.
 */
export const FOLLOW_MODAL_WHAT =
  "The prompter follows your reading and scrolls with you, instead of crawling at a fixed speed. It listens in this browser — no audio leaves your device.";

/** While the manifest is still landing. A fraction of a second, and named rather
 * than guessed at: see {@link followModal}. */
export const FOLLOW_CHECKING = "Checking what this browser already has…";

/* The two decisions behind the box — `followBox` and `followModal` — are in
 * `model-download.ts`, beside the phases and the one sentence they read. This
 * module has no imports and is meant to keep none: it is the room's decisions
 * with no DOM and no contract in them, and reaching for `modelOffer` from here
 * would pull React through a file whose whole point is that nothing does. */

/**
 * The line under the script box: what is loaded, how long it is, and how long it
 * will take to say.
 *
 * The rate is passed in rather than assumed. There is exactly one reading rate
 * in this product — the constant-speed WPM that Settings changes and the
 * prompter scrolls at — and a second one written into this line would be a
 * disagreement nobody could see, for everybody who moved the slider.
 */
export function scriptSummary(input: {
  name: string | undefined;
  words: number;
  wpm: number;
}): string | null {
  const { name, words, wpm } = input;
  if (words <= 0) return null;
  const counted = `${words} ${words === 1 ? "word" : "words"}`;
  const parts = [...(name === undefined ? [] : [shorten(name)]), counted];
  if (wpm > 0) {
    // Truncated, and floored at a second. Truncation is what makes 412 words at
    // 140 read `~2:56` rather than `~2:57` — it is an estimate with a tilde in
    // front of it, so rounding up to cross a minute boundary would be precision
    // it does not have. The floor is the other end of the same thought: `~0:00`
    // for a two-word script reads as a broken estimate rather than a quick read.
    const seconds = Math.max(1, Math.floor((words / wpm) * 60));
    parts.push(`~${estimate(seconds)} at ${wpm} wpm`);
  }
  return parts.join(" · ");
}

/** As much of a name as belongs on one line beside two other facts. */
const LONGEST_NAME = 28;

/**
 * A document's name, cut down to something that reads as a name.
 *
 * A Google Doc arrives with a real title and needs none of this. A pasted script
 * has no title at all, so the name falls back to its own first line — which is a
 * *sentence*, and the whole of it: "Good morning, and welcome back to the
 * channel. · 49 words · ~0:22 at 130 wpm" wrapped to two lines and read as a
 * quotation rather than as a label. It is still worth showing, because it is what
 * the recording will be named after (FR-48), so it is cut rather than dropped.
 *
 * Cut at a space where there is one near the end, so the result breaks between
 * words rather than through one.
 */
function shorten(name: string): string {
  if (name.length <= LONGEST_NAME) return name;
  const cut = name.slice(0, LONGEST_NAME);
  const space = cut.lastIndexOf(" ");
  return `${(space > LONGEST_NAME / 2 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/**
 * `m:ss`, and not `formatElapsed`.
 *
 * They look like the same job and are not. `formatElapsed` pads the minutes
 * because it counts a *running* take, where a timecode that changes width under
 * a reading eye is a timecode that jumps; this is a duration sitting still in a
 * sentence, where `~02:56` reads like a timestamp rather than like three
 * minutes. The brief writes it unpadded, and that is the reason.
 */
function estimate(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}
